import { useEffect, useState, type FormEvent } from "react";
import { api, callableErrorMessage } from "../lib/api";

type Props = {
  tenantId: string;
  initialName: string;
  initialPrimary: string;
  initialInstagram?: string;
  initialWebsite?: string;
  onSaved?: () => void;
};

const PALETTE = ["#4a6921", "#7ea838", "#65a30d", "#84cc16", "#b91c1c", "#0e7490", "#7c2d12", "#9a3412"];

export function BrandingEditor({
  tenantId,
  initialName,
  initialPrimary,
  initialInstagram = "",
  initialWebsite = "",
  onSaved
}: Props) {
  const [name, setName] = useState(initialName);
  const [primary, setPrimary] = useState(initialPrimary);
  const [instagram, setInstagram] = useState(initialInstagram);
  const [website, setWebsite] = useState(initialWebsite);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialName);
    setPrimary(initialPrimary);
    setInstagram(initialInstagram);
    setWebsite(initialWebsite);
  }, [initialName, initialPrimary, initialInstagram, initialWebsite]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateTenantBranding({
        tenantId,
        name: name.trim(),
        themePrimary: primary,
        instagram: instagram.trim() || undefined,
        website: website.trim() || undefined
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Nombre del comercio</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          minLength={2}
          maxLength={60}
          className="input"
        />
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-leaf-700">Color primario</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            className="h-12 w-16 cursor-pointer rounded-xl border-2 border-leaf-100"
          />
          <input
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            pattern="#[0-9a-fA-F]{6}"
            className="input w-32 font-mono"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setPrimary(c)}
              style={{ backgroundColor: c }}
              className={`h-8 w-8 rounded-full ring-2 transition ${
                primary.toLowerCase() === c.toLowerCase()
                  ? "ring-leaf-900 scale-110"
                  : "ring-transparent hover:scale-105"
              }`}
              title={c}
            />
          ))}
        </div>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Instagram (opcional)</span>
        <input
          value={instagram}
          onChange={(e) => setInstagram(e.target.value)}
          placeholder="@tucomercio"
          className="input"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Web (opcional)</span>
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://tucomercio.cl"
          className="input"
        />
      </label>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {saved && (
        <div className="rounded-2xl border border-leaf-200 bg-leaf-50 px-4 py-3 text-sm text-leaf-900">
          ✓ Guardado
        </div>
      )}

      <button type="submit" disabled={busy} className="btn-primary">
        {busy ? "Guardando…" : "Guardar"}
      </button>
    </form>
  );
}
